import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Table } from 'react-bootstrap';
import ReactChart from 'echarts-for-react';
import scanService from '../services/scanService';
import './Progression.css';

export default function Progression() {
  const navigate = useNavigate();
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [progressionData, setProgressionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clinical');

  // Check for selected patient data
  useEffect(() => {
    const patientData = sessionStorage.getItem('selectedPatient');
    if (patientData) {
      try {
        const patient = JSON.parse(patientData);
        setSelectedPatient(patient);
        fetchProgressionData(patient);
      } catch (error) {
        console.error('Error parsing patient data:', error);
        navigate('/admin');
      }
    } else {
      navigate('/admin');
    }
  }, [navigate]);

  // Fetch progression data for the selected patient
  const fetchProgressionData = async (patient) => {
    setLoading(true);
    try {
      const patientId = patient._id || patient.id;
      if (!patientId) {
        console.error('No patient ID found');
        setLoading(false);
        return;
      }

      // Fetch all scans for the patient
      const scansResponse = await scanService.getScansByCustomer(patientId);

      if (scansResponse.success && scansResponse.data) {
        // Sort scans by timepoint
        const sortedScans = scansResponse.data.sort((a, b) => {
          const aTimepoint = parseInt(a.timepoint?.replace('T', '') || '0');
          const bTimepoint = parseInt(b.timepoint?.replace('T', '') || '0');
          return aTimepoint - bTimepoint;
        });

        // Extract progression data
        const progressionData = {
          patient: patient,
          scans: sortedScans,
          timepoints: sortedScans.map(scan => scan.timepoint || 'T1'),
          edssScores: [],
          lesionVolumes: [],
          lesionCounts: [],
          greyMatterLesionVolumes: [],
          whiteMatterLesionVolumes: [],
          brainVolumes: [],
          scanImages: [],
          scannerInfo: [],
          scanDates: [],
          patientInfoFromScans: [], // Store patient info from each scan
          overallTrend: 'Stable',
          observationPeriod: calculateObservationPeriod(sortedScans),
          totalTimepoints: sortedScans.length,
          currentEDSS: patient.latestScanData?.edssScore || '2.5',
          currentLesions: patient.latestScanData?.totalLesionCount || '14'
        };

        // Fetch analysis results for each scan to get detailed data
        for (let i = 0; i < sortedScans.length; i++) {
          const scan = sortedScans[i];
          try {
            const analysisResponse = await scanService.getAnalysisResults(scan._id);
            if (analysisResponse.success && analysisResponse.data.analysisResult) {
              const biomarkers = analysisResponse.data.analysisResult.consolidated_results?.[0]?.biomarkers;
              const patientInfo = analysisResponse.data.analysisResult.consolidated_results?.[0]?.patientInformation;

              // Store patient info from scan response
              progressionData.patientInfoFromScans.push({
                patientId: patientInfo?.patientId,
                sex: patientInfo?.sex,
                ageAtScan: patientInfo?.ageAtScan,
                msSubtype: patientInfo?.msSubtype,
                edssScore: patientInfo?.edssScore,
                timepoint: patientInfo?.timepoint
              });

              progressionData.edssScores.push(patientInfo?.edssScore || 2.5);
              progressionData.lesionVolumes.push(biomarkers?.totalLesionVolume_mm3 ? (biomarkers.totalLesionVolume_mm3 / 1000).toFixed(1) : 15.5);
              progressionData.lesionCounts.push(biomarkers?.totalLesionCount || 14);
              progressionData.greyMatterLesionVolumes.push(biomarkers?.greyMatterLesionVolume_mm3 ? (biomarkers.greyMatterLesionVolume_mm3 / 1000).toFixed(1) : 2.3);
              progressionData.whiteMatterLesionVolumes.push(biomarkers?.whiteMatterLesionVolume_mm3 ? (biomarkers.whiteMatterLesionVolume_mm3 / 1000).toFixed(1) : 13.1);
              progressionData.brainVolumes.push(biomarkers?.totalBrainVolume_mm3 ? (biomarkers.totalBrainVolume_mm3 / 1000).toFixed(1) : 1345.0);

              // Extract scan images from asset paths
              const assetPaths = analysisResponse.data.analysisResult.consolidated_results?.[0]?.assetPaths;

              const scanImage = {
                axial: assetPaths?.visualizationSliceAxialPng,
                coronal: assetPaths?.visualizationSliceCoronalPng,
                sagittal: assetPaths?.visualizationSliceSagittalPng,
                multiplanar: assetPaths?.visualizationMultiplanarPng,
                rotation3d: assetPaths?.visualization3dRotationVideo
              };
              progressionData.scanImages.push(scanImage);

              // Extract scanner information
              const scannerInformation = analysisResponse.data.analysisResult.consolidated_results?.[0]?.scannerInformation;

              const scannerInfo = {
                manufacturer: scannerInformation?.FLAIR_MANUFACTURER || scannerInformation?.T1_MANUFACTURER || 'Philips Medical Systems',
                scanner: scannerInformation?.FLAIR_SCANNER || scannerInformation?.T1_SCANNER || 'Achieva dStream',
                matrix: scannerInformation?.FLAIR_MATRIX || scannerInformation?.T1_MATRIX || '[0, 256, 240, 0]',
                pixelSpacing: scannerInformation?.FLAIR_PIXEL_SPACING || scannerInformation?.T1_PIXEL_SPACING || '[1, 1]',
                sliceThickness: scannerInformation?.FLAIR_SLICE_THICKNESS || scannerInformation?.T1_SLICE_THICKNESS || '3 mm',
                fieldOfView: scannerInformation?.FLAIR_FOV || scannerInformation?.T1_FOV || '240 mm',
                repetitionTime: scannerInformation?.FLAIR_TR || scannerInformation?.T1_TR || '2000 ms',
                echoTime: scannerInformation?.FLAIR_TE || scannerInformation?.T1_TE || '80 ms'
              };
              progressionData.scannerInfo.push(scannerInfo);

              progressionData.scanDates.push(new Date(scan.createdAt || scan.analysisTimestamp).toLocaleDateString());
            } else {
              // Use mock data if analysis not available
              progressionData.edssScores.push(2.5 + (i * 0.5));
              progressionData.lesionVolumes.push(15.5 - (i * 1.5));
              progressionData.lesionCounts.push(14 - i);
              progressionData.greyMatterLesionVolumes.push(2.3 - (i * 0.1));
              progressionData.whiteMatterLesionVolumes.push(13.1 - (i * 0.5));
              progressionData.brainVolumes.push(1345.0 - (i * 4));

              // Mock scan images
              progressionData.scanImages.push({
                axial: null,
                coronal: null,
                sagittal: null,
                multiplanar: null,
                rotation3d: null
              });

              // Mock scanner information
              progressionData.scannerInfo.push({
                manufacturer: 'Philips Medical Systems',
                scanner: 'Achieva dStream',
                matrix: '[0, 256, 240, 0]',
                pixelSpacing: '[1, 1]',
                sliceThickness: '3 mm',
                fieldOfView: '240 mm',
                repetitionTime: '2000 ms',
                echoTime: '80 ms'
              });

              progressionData.scanDates.push(new Date().toLocaleDateString());
            }
          } catch (error) {
            console.error(`Error fetching analysis for scan ${scan._id}:`, error);
            // Use mock data as fallback
            progressionData.edssScores.push(2.5 + (i * 0.5));
            progressionData.lesionVolumes.push(15.5 - (i * 1.5));
            progressionData.lesionCounts.push(14 - i);
            progressionData.greyMatterLesionVolumes.push(2.3 - (i * 0.1));
            progressionData.whiteMatterLesionVolumes.push(13.1 - (i * 0.5));
            progressionData.brainVolumes.push(1345.0 - (i * 4));

            // Mock scan images
            progressionData.scanImages.push({
              axial: null,
              coronal: null,
              sagittal: null,
              multiplanar: null,
              rotation3d: null
            });

            // Mock scanner information
            progressionData.scannerInfo.push({
              manufacturer: 'Philips',
              scanner: 'Achieva',
              matrix: '256x256'
            });

            progressionData.scanDates.push(new Date().toLocaleDateString());
          }
        }

        // Calculate overall trend
        if (progressionData.edssScores.length >= 2) {
          const firstEDSS = progressionData.edssScores[0];
          const lastEDSS = progressionData.edssScores[progressionData.edssScores.length - 1];
          const edssChange = lastEDSS - firstEDSS;

          if (edssChange > 0.5) {
            progressionData.overallTrend = 'Progressive';
          } else if (edssChange < -0.5) {
            progressionData.overallTrend = 'Improving';
          } else {
            progressionData.overallTrend = 'Stable';
          }
        }
        setProgressionData(progressionData);
      } else {
        // Use mock data if no scans found
        const mockProgressionData = {
          patient: patient,
          scans: [],
          timepoints: ['T1', 'T2', 'T3'],
          edssScores: [3.5, 3.5, 2.5],
          lesionVolumes: [20, 18, 15.5],
          lesionCounts: [16, 15, 14],
          greyMatterLesionVolumes: [2.5, 2.4, 2.3],
          whiteMatterLesionVolumes: [17.5, 15.6, 13.1],
          brainVolumes: [1345.0, 1341.0, 1337.0],
          scanImages: [
            { axial: null, coronal: null, sagittal: null, multiplanar: null, rotation3d: null },
            { axial: null, coronal: null, sagittal: null, multiplanar: null, rotation3d: null },
            { axial: null, coronal: null, sagittal: null, multiplanar: null, rotation3d: null }
          ],
          scannerInfo: [
            { manufacturer: 'Philips Medical Systems', scanner: 'Achieva dStream', matrix: '[0, 256, 240, 0]', pixelSpacing: '[1, 1]', sliceThickness: '3 mm', fieldOfView: '240 mm', repetitionTime: '2000 ms', echoTime: '80 ms' },
            { manufacturer: 'Philips Healthcare', scanner: 'Ingenia', matrix: '[0, 256, 240, 0]', pixelSpacing: '[1, 1]', sliceThickness: '3 mm', fieldOfView: '240 mm', repetitionTime: '2000 ms', echoTime: '80 ms' },
            { manufacturer: 'Philips Healthcare', scanner: 'Ingenia', matrix: '[0, 256, 240, 0]', pixelSpacing: '[1, 1]', sliceThickness: '3 mm', fieldOfView: '240 mm', repetitionTime: '2000 ms', echoTime: '80 ms' }
          ],
          scanDates: ['2023-01-15', '2023-07-15', '2024-01-15'],
          overallTrend: 'Stable',
          observationPeriod: '1.4 years',
          totalTimepoints: 3,
          currentEDSS: '2.5',
          currentLesions: '14'
        };
        setProgressionData(mockProgressionData);
      }
    } catch (error) {
      console.error('Error fetching progression data:', error);
      // Use mock data as fallback
      const mockProgressionData = {
        patient: patient,
        scans: [],
        timepoints: ['T1', 'T2', 'T3'],
        edssScores: [3.5, 3.5, 2.5],
        lesionVolumes: [20, 18, 15.5],
        lesionCounts: [16, 15, 14],
        greyMatterLesionVolumes: [2.5, 2.4, 2.3],
        whiteMatterLesionVolumes: [17.5, 15.6, 13.1],
        brainVolumes: [1345.0, 1341.0, 1337.0],
        scanImages: [
          { axial: null, coronal: null, sagittal: null, multiplanar: null, rotation3d: null },
          { axial: null, coronal: null, sagittal: null, multiplanar: null, rotation3d: null },
          { axial: null, coronal: null, sagittal: null, multiplanar: null, rotation3d: null }
        ],
        scanDates: ['2023-01-15', '2023-07-15', '2024-01-15'],
        overallTrend: 'Stable',
        observationPeriod: '1.4 years',
        totalTimepoints: 3,
        currentEDSS: '2.5',
        currentLesions: '14'
      };
      setProgressionData(mockProgressionData);
    } finally {
      setLoading(false);
    }
  };

  // Calculate observation period
  const calculateObservationPeriod = (scans) => {
    if (scans.length < 2) return '0 years';

    const firstScan = new Date(scans[0].createdAt || scans[0].analysisTimestamp);
    const lastScan = new Date(scans[scans.length - 1].createdAt || scans[scans.length - 1].analysisTimestamp);
    const diffTime = Math.abs(lastScan - firstScan);
    const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365);

    return `${diffYears.toFixed(1)} years`;
  };

  // Download Technical Specs as CSV
  const downloadTechnicalSpecsCSV = () => {
    if (!progressionData || !progressionData.scans.length) {
      alert('No technical data available to download');
      return;
    }

    const patientId = progressionData.patientInfoFromScans.length > 0
      ? progressionData.patientInfoFromScans[0].patientId
      : progressionData.patient.patientId || 'P1';

    // Prepare CSV data
    const csvHeaders = [
      'Timepoint',
      'Scan Date',
      'Manufacturer',
      'Scanner Model',
      'Matrix',
      'Rows',
      'Columns',
      'Pixel Spacing',
      'Slice Thickness',
      'Field of View',
      'Repetition Time',
      'Echo Time',
      'EDSS Score',
      'Lesion Count',
      'Lesion Volume (mL)',
      'Brain Volume (mL)'
    ];

    const csvRows = progressionData.scans.map((scan, index) => {
      const scannerInfo = progressionData.scannerInfo[index] || {};
      const matrix = scannerInfo.matrix || '[0, 256, 240, 0]';
      const pixelSpacing = scannerInfo.pixelSpacing || '[1, 1]';
      const sliceThickness = scannerInfo.sliceThickness || '3 mm';

      // Parse matrix to get rows and columns
      const matrixArray = matrix.replace(/[\[\]]/g, '').split(',').map(n => parseInt(n.trim()));
      const rows = matrixArray[1] || 256;
      const columns = matrixArray[2] || 240;

      return [
        scan.timepoint || `T${index + 1}`,
        progressionData.scanDates[index] || 'N/A',
        scannerInfo.manufacturer || 'Philips Medical Systems',
        scannerInfo.scanner || 'Achieva dStream',
        matrix,
        rows,
        columns,
        pixelSpacing,
        sliceThickness,
        scannerInfo.fieldOfView || '240 mm',
        scannerInfo.repetitionTime || '2000 ms',
        scannerInfo.echoTime || '80 ms',
        progressionData.edssScores[index] || 'N/A',
        progressionData.lesionCounts[index] || 'N/A',
        progressionData.lesionVolumes[index] || 'N/A',
        progressionData.brainVolumes[index] || 'N/A'
      ];
    });

    // Convert to CSV format
    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Technical_Specs_${patientId}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download DICOM Metadata as JSON
  const downloadDICOMetadata = () => {
    if (!progressionData || !progressionData.scans.length) {
      alert('No DICOM metadata available to download');
      return;
    }

    const patientId = progressionData.patientInfoFromScans.length > 0
      ? progressionData.patientInfoFromScans[0].patientId
      : progressionData.patient.patientId || 'P1';

    // Prepare DICOM metadata
    const dicomMetadata = {
      patient: {
        patientId: patientId,
        sex: progressionData.patientInfoFromScans.length > 0
          ? progressionData.patientInfoFromScans[0].sex
          : progressionData.patient.sex,
        age: progressionData.patientInfoFromScans.length > 0
          ? progressionData.patientInfoFromScans[0].ageAtScan
          : progressionData.patient.age,
        msSubtype: progressionData.patientInfoFromScans.length > 0
          ? progressionData.patientInfoFromScans[0].msSubtype
          : progressionData.patient.msSubType
      },
      study: {
        studyDate: new Date().toISOString().split('T')[0],
        studyDescription: 'Multiple Sclerosis Progression Study',
        totalTimepoints: progressionData.totalTimepoints,
        observationPeriod: progressionData.observationPeriod,
        overallTrend: progressionData.overallTrend
      },
      series: progressionData.scans.map((scan, index) => {
        const scannerInfo = progressionData.scannerInfo[index] || {};
        return {
          seriesNumber: index + 1,
          timepoint: scan.timepoint || `T${index + 1}`,
          scanDate: progressionData.scanDates[index] || 'N/A',
          modality: 'MR',
          seriesDescription: 'MS Progression Scan',
          scanner: {
            manufacturer: scannerInfo.manufacturer || 'Philips Medical Systems',
            model: scannerInfo.scanner || 'Achieva dStream',
            softwareVersion: 'Unknown'
          },
          acquisition: {
            matrix: scannerInfo.matrix || '[0, 256, 240, 0]',
            pixelSpacing: scannerInfo.pixelSpacing || '[1, 1]',
            sliceThickness: scannerInfo.sliceThickness || '3 mm',
            fieldOfView: scannerInfo.fieldOfView || '240 mm',
            repetitionTime: scannerInfo.repetitionTime || '2000 ms',
            echoTime: scannerInfo.echoTime || '80 ms'
          },
          analysis: {
            edssScore: progressionData.edssScores[index] || 'N/A',
            lesionCount: progressionData.lesionCounts[index] || 'N/A',
            lesionVolume: progressionData.lesionVolumes[index] || 'N/A',
            brainVolume: progressionData.brainVolumes[index] || 'N/A'
          }
        };
      }),
      exportInfo: {
        exportedAt: new Date().toISOString(),
        exportedBy: 'MRI AI Analysis System',
        version: '1.0'
      }
    };

    // Convert to JSON and download
    const jsonContent = JSON.stringify(dicomMetadata, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `DICOM_Metadata_${patientId}_${new Date().toISOString().split('T')[0]}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Generate dynamic chart options
  const getEDSSChartOption = () => {
    if (!progressionData) return {};

    return {
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          const p = params[0];
          return `
          <div>
            <span style="color:#000">${p.axisValue}</span><br/>
              <span style="color:#007bff;">EDSS Score: ${p.data}</span>
          </div>
        `;
        },
      },
      grid: { left: 50, right: 30, top: 10, bottom: 40 },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: progressionData.timepoints,
        axisLine: {
          show: true,
          lineStyle: { color: "#858585ff", width: 1 },
        },
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "#ccc" },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: 10,
        interval: 2,
        name: "EDSS Score",
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: {
          fontSize: 14,
          fontWeight: "500",
          color: "#333",
        },
        axisLine: {
          show: true,
          lineStyle: { color: "#858585ff", width: 1 },
        },
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "#ccc" },
        },
      },
      series: [
        {
          data: progressionData.edssScores,
          type: "line",
          symbol: "circle",
          symbolSize: 12,
          lineStyle: {
            color: "#007bff",
            width: 2,
          },
          itemStyle: {
            color: "#007bff",
            borderColor: "#fff",
            borderWidth: 2,
          },
          emphasis: {
            itemStyle: {
              color: "#fff",
              borderColor: "#007bff",
              borderWidth: 3,
            },
          },
        },
      ],
    };
  };

  const getLesionCountChartOption = () => {
    if (!progressionData) return {};

    return {
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          const point = params[0];
          return `
            <div style="
              padding:8px 12px;
              border:1px solid #ddd;
              border-radius:6px;
              background:#fff;
              box-shadow:0 2px 6px rgba(0,0,0,0.15);
            ">
              <div style="font-weight:500; margin-bottom:4px;">${point.name}</div>
              <div style="color:#007bff;">Lesion Count: ${point.value}</div>
            </div>
          `;
        },
        backgroundColor: "transparent",
        borderWidth: 0,
        extraCssText: "box-shadow:none;",
      },
      grid: {
        left: 50,
        right: 25,
        bottom: 40,
        top: 20,
      },
      xAxis: {
        type: "category",
        data: progressionData.timepoints,
        axisLine: { lineStyle: { color: "#333", width: 1 } },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "#ddd" },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: Math.max(...progressionData.lesionCounts) + 5,
        interval: Math.ceil(Math.max(...progressionData.lesionCounts) / 4),
        name: "Count",
        axisLine: { lineStyle: { color: "#333", width: 1 } },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "#ddd" },
        },
      },
      series: [
        {
          data: progressionData.lesionCounts,
          type: "bar",
          barWidth: '60%',
          itemStyle: {
            color: "#007bff",
            borderRadius: [4, 4, 0, 0],
          },
          emphasis: {
            itemStyle: {
              color: "#0056b3",
            },
          },
        },
      ],
    };
  };

  const getLesionDistributionChartOption = () => {
    if (!progressionData) return {};

    // Get latest scan data for distribution
    const latestGreyMatter = progressionData.greyMatterLesionVolumes?.[progressionData.greyMatterLesionVolumes.length - 1] || 2.3;
    const latestWhiteMatter = progressionData.whiteMatterLesionVolumes?.[progressionData.whiteMatterLesionVolumes.length - 1] || 13.1;

    return {
      tooltip: {
        trigger: "item",
        formatter: "{a} <br/>{b}: {c} mL ({d}%)",
      },
      legend: {
        orient: "horizontal",
        bottom: 10,
        data: ["Grey Matter Lesions", "White Matter Lesions"],
      },
      series: [
        {
          name: "Lesion Distribution",
          type: "pie",
          radius: ["40%", "70%"],
          center: ["50%", "45%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            show: false,
            position: "center",
          },
          emphasis: {
            label: {
              show: true,
              fontSize: "16",
              fontWeight: "bold",
            },
          },
          labelLine: {
            show: false,
          },
          data: [
            {
              value: latestGreyMatter,
              name: "Grey Matter Lesions",
              itemStyle: { color: "#6f42c1" },
            },
            {
              value: latestWhiteMatter,
              name: "White Matter Lesions",
              itemStyle: { color: "#007bff" },
            },
          ],
        },
      ],
    };
  };

  const getLesionVolumeChartOption = () => {
    if (!progressionData) return {};

    return {
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          const point = params[0];
          return `
            <div style="
              padding:8px 12px;
              border:1px solid #ddd;
              border-radius:6px;
              background:#fff;
              box-shadow:0 2px 6px rgba(0,0,0,0.15);
            ">
              <div style="font-weight:500; margin-bottom:4px;">${point.name}</div>
              <div style="color:#28a745;">Lesion Volume: ${point.value} mL</div>
            </div>
          `;
        },
        backgroundColor: "transparent",
        borderWidth: 0,
        extraCssText: "box-shadow:none;",
      },
      grid: {
        left: 50,
        right: 25,
        bottom: 40,
        top: 20,
      },
      xAxis: {
        type: "category",
        data: progressionData.timepoints,
        boundaryGap: false,
        axisLine: { lineStyle: { color: "#333", width: 1 } },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "#ddd" },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: Math.max(...progressionData.lesionVolumes) + 5,
        interval: Math.ceil(Math.max(...progressionData.lesionVolumes) / 4),
        name: "Volume (mL)",
        axisLine: { lineStyle: { color: "#333", width: 1 } },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "#ddd" },
        },
      },
      series: [
        {
          data: progressionData.lesionVolumes,
          type: "line",
          smooth: true,
          areaStyle: { color: "rgba(40, 167, 69, 0.2)" },
          lineStyle: { color: "#28a745", width: 2 },
          symbol: "circle",
          symbolSize: 8,
          itemStyle: {
            color: "#28a745",
            borderWidth: 2,
            borderColor: "#fff",
          },
        },
      ],
    };
  };

  const getCombinedMetricsChartOption = () => {
    if (!progressionData) return {};

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
        },
        formatter: (params) => {
          let result = `<div style="padding:8px 12px; border:1px solid #ddd; border-radius:6px; background:#fff; box-shadow:0 2px 6px rgba(0,0,0,0.15);">`;
          result += `<div style="font-weight:500; margin-bottom:4px;">${params[0].axisValue}</div>`;
          params.forEach(param => {
            result += `<div style="color:${param.color};">${param.seriesName}: ${param.value}${param.seriesName.includes('Volume') ? ' mL' : ''}</div>`;
          });
          result += `</div>`;
          return result;
        },
        backgroundColor: "transparent",
        borderWidth: 0,
        extraCssText: "box-shadow:none;",
      },
      legend: {
        data: ["EDSS Score", "Lesion Volume (mL)"],
        bottom: 10,
      },
      grid: {
        left: 50,
        right: 50,
        bottom: 60,
        top: 20,
      },
      xAxis: {
        type: "category",
        data: progressionData.timepoints,
        axisLine: { lineStyle: { color: "#333", width: 1 } },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: { type: "dotted", color: "#ddd" },
        },
      },
      yAxis: [
        {
          type: "value",
          name: "EDSS Score",
          position: "left",
          min: 0,
          max: Math.max(...progressionData.edssScores) + 1,
          interval: Math.ceil(Math.max(...progressionData.edssScores) / 4),
          axisLine: { lineStyle: { color: "#007bff", width: 1 } },
          axisTick: { show: false },
          splitLine: {
            show: true,
            lineStyle: { type: "dotted", color: "#ddd" },
          },
        },
        {
          type: "value",
          name: "Lesion Volume (mL)",
          position: "right",
          min: 0,
          max: Math.max(...progressionData.lesionVolumes) + 5,
          interval: Math.ceil(Math.max(...progressionData.lesionVolumes) / 4),
          axisLine: { lineStyle: { color: "#28a745", width: 1 } },
          axisTick: { show: false },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: "EDSS Score",
          type: "line",
          yAxisIndex: 0,
          data: progressionData.edssScores,
          lineStyle: { color: "#007bff", width: 2 },
          symbol: "circle",
          symbolSize: 8,
          itemStyle: {
            color: "#007bff",
            borderWidth: 2,
            borderColor: "#fff",
          },
        },
        {
          name: "Lesion Volume (mL)",
          type: "line",
          yAxisIndex: 1,
          data: progressionData.lesionVolumes,
          lineStyle: { color: "#28a745", width: 2 },
          symbol: "circle",
          symbolSize: 8,
          itemStyle: {
            color: "#28a745",
            borderWidth: 2,
            borderColor: "#fff",
          },
        },
      ],
    };
  };



  if (loading) {
    return (
      <div className="progression">
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only"></span>
          </div>
        </div>
      </div>
    );
  }

  if (!progressionData) {
    return (
      <div className="progression">
        <div className="error-container">
          <p>No progression data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="progression">
      <div className='custom-card'>
        <div className='custom-card-header'>
          <div className='custom-card-header-text'>
            <h2>
              Disease Progression Tracking
            </h2>
            <p>Longitudinal analysis of clinical and imaging biomarkers.</p>
          </div>
        </div>
        <div className="analysis-wrapper">
          <div className="item">
            <span className={`stat-value trend-${progressionData.overallTrend.toLowerCase()}`}>
              {progressionData.overallTrend}
            </span>
            <p className="title mt-2"> Overall Trend</p>
          </div>
          <div className="item ">
            <h3 className="">{progressionData.totalTimepoints}</h3>
            <p className="title">Total Timepoints</p>

          </div>
          <div className="item ">
            <h3 className="">{progressionData.currentEDSS}</h3>
            <p className="title">Current EDSS</p>
          </div>
          <div className="item ">
            <h3 className="">{progressionData.currentLesions} ({progressionData.lesionVolumes[progressionData.lesionVolumes.length - 1] || '15.5'} mL)</h3>
            <p className="title">Current Lesions</p>
          </div>
        </div>
      </div>
      <div className='text-center'>
        <div className="progression-tabs mt-4">
          <button
            className={`tab-button ${activeTab === 'clinical' ? 'active' : ''}`}
            onClick={() => setActiveTab('clinical')}
          >
            Clinical Metrics
          </button>
          <button
            className={`tab-button ${activeTab === 'imaging' ? 'active' : ''}`}
            onClick={() => setActiveTab('imaging')}
          >
            Imaging Biomarkers
          </button>
          <button
            className={`tab-button ${activeTab === 'comparison' ? 'active' : ''}`}
            onClick={() => setActiveTab('comparison')}
          >
            Scan Comparison
          </button>
          <button
            className={`tab-button ${activeTab === 'technical' ? 'active' : ''}`}
            onClick={() => setActiveTab('technical')}
          >
            Technical Details
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="progression-content">
        {activeTab === 'clinical' && (
          <Row>
            {/* EDSS Score Progression Chart */}
            <Col md={6}>
              <div className="chart-card">
                <div className="chart-header">
                  <h3>EDSS Score Progression</h3>
                  <p>Expanded Disability Status Scale over time</p>
                </div>
                <div className="chart-container">
                  <ReactChart option={getEDSSChartOption()} />
                </div>
              </div>
            </Col>

            {/* Patient Profile Card */}
            <Col md={6}>
              <div className="patient-profile-card">
                <div className="profile-header">
                  <h3>Patient Profile</h3>
                </div>
                <div className="profile-content">
                  <div className="profile-item">
                    <span className="profile-label">Patient ID</span>
                    <span className="profile-value">
                      {progressionData.patientInfoFromScans.length > 0
                        ? progressionData.patientInfoFromScans[0].patientId
                        : progressionData.patient.patientId || progressionData.patient.id || 'P1'}
                    </span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">Sex</span>
                    <span className="profile-value">
                      {progressionData.patientInfoFromScans.length > 0
                        ? (progressionData.patientInfoFromScans[0].sex === 'M' ? 'Male' : progressionData.patientInfoFromScans[0].sex === 'F' ? 'Female' : 'N/A')
                        : (progressionData.patient.sex === 'M' ? 'Male' : progressionData.patient.sex === 'F' ? 'Female' : 'N/A')}
                    </span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">MS Subtype</span>
                    <span className="profile-value">
                      {progressionData.patientInfoFromScans.length > 0
                        ? (progressionData.patientInfoFromScans[0].msSubtype === 'RRMS' ? 'Relapsing-Remitting MS' :
                          progressionData.patientInfoFromScans[0].msSubtype === 'PPMS' ? 'Primary Progressive MS' :
                            progressionData.patientInfoFromScans[0].msSubtype === 'SPMS' ? 'Secondary Progressive MS' :
                              progressionData.patientInfoFromScans[0].msSubtype || 'Unknown')
                        : (progressionData.patient.msSubType === 'RRMS' ? 'Relapsing-Remitting MS' :
                          progressionData.patient.msSubType === 'PPMS' ? 'Primary Progressive MS' :
                            progressionData.patient.msSubType === 'SPMS' ? 'Secondary Progressive MS' :
                              progressionData.patient.msSubType || 'Unknown')}
                    </span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">Current Age</span>
                    <span className="profile-value">
                      {progressionData.patientInfoFromScans.length > 0
                        ? `${progressionData.patientInfoFromScans[0].ageAtScan || 'N/A'} years`
                        : `${progressionData.patient.age || progressionData.patient.currentAge || 'N/A'} years`}
                    </span>
                  </div>
                  <div className="profile-item">
                    <span className="profile-label">Latest Scan</span>
                    <span className="profile-value">
                      {progressionData.scanDates[progressionData.scanDates.length - 1] || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </Col>

            {/* Lesion Volume Progression Chart */}
            <Col md={12}>
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Lesion Volume Progression</h3>
                  <p>Total lesion burden over time (mL)</p>
                </div>
                <div className="chart-container">
                  <ReactChart option={getLesionVolumeChartOption()} />
                </div>
              </div>
            </Col>
          </Row>
        )}

        {activeTab === 'imaging' && (
          <Row>
            {/* Lesion Count Chart */}
            <Col md={6}>
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Lesion Count - {
                    progressionData.patientInfoFromScans.length > 0
                      ? progressionData.patientInfoFromScans[0].patientId
                      : progressionData.patient.patientId || progressionData.patient.id || 'P1'
                  }</h3>
                  <p>Number of lesions per timepoint</p>
                </div>
                <div className="chart-container">
                  <ReactChart option={getLesionCountChartOption()} />
                </div>
              </div>
            </Col>

            {/* Lesion Distribution Chart */}
            <Col md={6}>
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Lesion Distribution (GM vs WM)</h3>
                  <p>Latest timepoint tissue breakdown (mL)</p>
                </div>
                <div className="chart-container">
                  <ReactChart option={getLesionDistributionChartOption()} />
                </div>
              </div>
            </Col>

            {/* Combined Metrics Chart */}
            <Col md={12}>
              <div className="chart-card">
                <div className="chart-header">
                  <h3>Combined Metrics: EDSS vs Lesion Volume</h3>
                  <p>Dual-axis comparison of clinical and imaging metrics</p>
                </div>
                <div className="chart-container">
                  <ReactChart option={getCombinedMetricsChartOption()} />
                </div>
              </div>
            </Col>
          </Row>
        )}

        {activeTab === 'comparison' && (
          <div className="scan-comparison">
            <Row>
              {progressionData.scans.map((scan, index) => {
                const scanDate = progressionData.scanDates[index] || new Date().toLocaleDateString();
                const edssScore = progressionData.edssScores[index] || 2.5;
                const lesionCount = progressionData.lesionCounts[index] || 14;
                const lesionVolume = progressionData.lesionVolumes[index] || 15.5;
                const brainVolume = progressionData.brainVolumes?.[index] || 1345.0;

                // Calculate changes from T1 (baseline)
                const baselineIndex = 0;
                const edssChange = index > 0 ? edssScore - progressionData.edssScores[baselineIndex] : 0;
                const lesionCountChange = index > 0 ? lesionCount - progressionData.lesionCounts[baselineIndex] : 0;
                const lesionVolumeChange = index > 0 ? lesionVolume - progressionData.lesionVolumes[baselineIndex] : 0;
                const brainVolumeChange = index > 0 ? brainVolume - (progressionData.brainVolumes?.[baselineIndex] || 1345.0) : 0;

                return (
                  <Col md={4} key={scan._id || index}>
                    <div className="scan-comparison-card">
                      <div className="scan-header">
                        <div className="scan-title">
                          <h3>{scan.timepoint || `T${index + 1}`}</h3>
                          <span className="scan-date">{scanDate}</span>
                        </div>
                        <div className="prediction-badge">
                          <span>Predicted</span>
                        </div>
                      </div>

                      <div className="scan-image">
                        {(() => {
                          const scanImage = progressionData.scanImages[index];

                          // Try multiplanar first, then fallback to axial, coronal, or sagittal
                          const imageUrl = scanImage?.multiplanar || scanImage?.axial || scanImage?.coronal || scanImage?.sagittal;

                          return imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={`${scan.timepoint || `T${index + 1}`} Brain Scan`}
                              className="brain-scan-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null;
                        })()}
                        <div className="brain-model-placeholder" style={{
                          display: (progressionData.scanImages[index]?.multiplanar || progressionData.scanImages[index]?.axial || progressionData.scanImages[index]?.coronal || progressionData.scanImages[index]?.sagittal) ? 'none' : 'flex'
                        }}>
                          <div className="brain-icon">🧠</div>
                          <div className="placeholder-text">No scan image available</div>
                        </div>
                      </div>

                      <div className="clinical-metrics">
                        <h4>Clinical Metrics</h4>
                        <div className="metric-item">
                          <span className="metric-label">EDSS Score</span>
                          <span className="metric-value">
                            {edssScore}
                            {edssChange !== 0 && (
                              <span className={`metric-change ${edssChange > 0 ? 'positive' : 'negative'}`}>
                                ({edssChange > 0 ? '+' : ''}{edssChange.toFixed(1)})
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="metric-item">
                          <span className="metric-label">Lesion Count</span>
                          <span className="metric-value">
                            {lesionCount}
                            {lesionCountChange !== 0 && (
                              <span className={`metric-change ${lesionCountChange > 0 ? 'positive' : 'negative'}`}>
                                ({lesionCountChange > 0 ? '+' : ''}{lesionCountChange})
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="metric-item">
                          <span className="metric-label">Lesion Volume</span>
                          <span className="metric-value">
                            {lesionVolume} mL
                            {lesionVolumeChange !== 0 && (
                              <span className={`metric-change ${lesionVolumeChange > 0 ? 'positive' : 'negative'}`}>
                                ({lesionVolumeChange > 0 ? '+' : ''}{lesionVolumeChange.toFixed(1)} mL)
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="metric-item">
                          <span className="metric-label">Brain Volume</span>
                          <span className="metric-value">
                            {brainVolume} mL
                            {brainVolumeChange !== 0 && (
                              <span className={`metric-change ${brainVolumeChange > 0 ? 'positive' : 'negative'}`}>
                                ({brainVolumeChange > 0 ? '+' : ''}{brainVolumeChange.toFixed(1)} mL)
                              </span>
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="scanner-info">
                        <h4>Scanner Information</h4>
                        <div className="scanner-item">
                          <span className="scanner-label">Manufacturer</span>
                          <span className="scanner-value">{progressionData.scannerInfo[index]?.manufacturer || 'Philips'}</span>
                        </div>
                        <div className="scanner-item">
                          <span className="scanner-label">Scanner</span>
                          <span className="scanner-value">
                            {progressionData.scannerInfo[index]?.scanner || 'Unknown'}
                          </span>
                        </div>
                        <div className="scanner-item">
                          <span className="scanner-label">Matrix</span>
                          <span className="scanner-value">
                            {progressionData.scannerInfo[index]?.matrix || '256x256'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        )}

        {activeTab === 'technical' && (
          <div className="tab-content">
            <Row>
              {/* Scanner Summary */}
              <Col md={6}>
                <div className="chart-card">
                  <div className="chart-header">
                    <h3>Scanner Summary - {progressionData.patientInfoFromScans.length > 0 ? progressionData.patientInfoFromScans[0].patientId : progressionData.patient.patientId || 'P1'}</h3>
                    <p>Primary scanner information</p>
                  </div>
                  <div className="chart-container">
                    <div className="scanner-summary">
                      <div className="summary-item">
                        <span className="summary-label">Primary Scanner</span>
                        <span className="summary-value">
                          {progressionData.scannerInfo.length > 0
                            ? `${progressionData.scannerInfo[0]?.manufacturer || 'Philips'} ${progressionData.scannerInfo[0]?.scanner || 'Healthcare'}`
                            : 'Philips Healthcare Ingenia'
                          }
                        </span>
                      </div>

                      <div className="summary-item">
                        <span className="summary-label">Total Scans</span>
                        <span className="summary-value">{progressionData.totalTimepoints} timepoints</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Export Options */}
              <Col md={6}>
                <div className="chart-card">
                  <div className="chart-header">
                    <h3>Export Options</h3>
                    <p>Download technical data</p>
                  </div>
                  <div className="chart-container">
                    <div className="export-options">
                      <button className="export-btn" onClick={downloadTechnicalSpecsCSV}>
                        <span className="export-icon">📊</span>
                        Download Technical Specs (CSV)
                      </button>
                      <button className="export-btn" onClick={downloadDICOMetadata}>
                        <span className="export-icon">📁</span>
                        Download DICOM Metadata
                      </button>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Complete Technical Specifications */}
            <Row className="mt-4">
              <Col md={12}>
                <div className="custom-card">
                  <div className="custom-card-header">
                    <div className='custom-card-header-text'>
                      <h3>Complete Technical Specifications</h3>
                      <p>Detailed scanner parameters for all timepoints</p>
                    </div>
                  </div>
                  <div className="custom-table-wrapper">
                    <Table hover responsive className="custom-table">
                      <thead>
                        <tr>
                          <th>Timepoint</th>
                          <th>Manufacturer</th>
                          <th>Scanner Model</th>
                          <th>Matrix</th>
                          <th>Rows</th>
                          <th>Columns</th>
                          <th>Pixel Spacing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {progressionData.scans.map((scan, index) => {
                          const scannerInfo = progressionData.scannerInfo[index] || {};
                          const matrix = scannerInfo.matrix || '[0, 256, 240, 0]';
                          const pixelSpacing = scannerInfo.pixelSpacing || '[1, 1]';

                          // Parse matrix to get rows and columns
                          const matrixArray = matrix.replace(/[\[\]]/g, '').split(',').map(n => parseInt(n.trim()));
                          const rows = matrixArray[1] || 256;
                          const columns = matrixArray[2] || 240;

                          return (
                            <tr key={scan._id || index}>
                              <td>{scan.timepoint || `T${index + 1}`}</td>
                              <td>{scannerInfo.manufacturer || 'Philips Medical Systems'}</td>
                              <td>{scannerInfo.scanner || 'Achieva dStream'}</td>
                              <td>{matrix}</td>
                              <td>{rows}</td>
                              <td>{columns}</td>
                              <td>{pixelSpacing}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
}

